import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL, apiUrl } from '../config/api';

const Produse = ({ produse, setProduse, currentUser, statiiLucru, currentUserRole }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  
  // Modal and form states
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    denumire: '',
    neckSize: '',
    culoare: '',
    bucPerSac: 0,
    statiiLucru: {}
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    denumire: '',
    neckSize: [],
    culoare: []
  });
  
  // Sort states
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'default'
  });

  // Dropdown visibility states
  const [dropdownOpen, setDropdownOpen] = useState({
    neckSize: false,
    culoare: false
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [inputValue, setInputValue] = useState('25');

  // Role helpers
  const isOperator = (currentUserRole === 'Operatori' || currentUserRole === 'Operator');
  const isAdmin = (currentUserRole === 'Admin' || currentUserRole === 'Administrator');

  useEffect(() => {
    fetchProduse();
    const onRefresh = () => fetchProduse();
    window.addEventListener('refresh-produse', onRefresh);
    return () => window.removeEventListener('refresh-produse', onRefresh);
  }, []);

  // Compute unique stations by name and a representative mapping for duplicates.
  // If two stations share the same `denumire`, the first encountered becomes the representative.
  const { uniqueStatii, stationRepMap } = useMemo(() => {
    const unique = [];
    const repByName = {};
    const repMap = {};
    for (const s of statiiLucru) {
      if (!repByName[s.denumire]) {
        repByName[s.denumire] = s.id;
        unique.push(s);
        repMap[s.id] = s.id;
      } else {
        // map this duplicate station id to the representative id
        repMap[s.id] = repByName[s.denumire];
      }
    }
    return { uniqueStatii: unique, stationRepMap: repMap };
  }, [statiiLucru]);

  const fetchProduse = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/produse`);
      setProduse(response.data);
    } catch (error) {
      console.error('Error fetching produse:', error);
      setError('Eroare la încărcarea produselor');
    }
  };

  const handleCSVImport = (event) => {
    if (isOperator) {
      setError('Operatorii nu pot importa produse.');
      event.target.value = '';
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Vă rugăm să selectați un fișier CSV valid');
      return;
    }

    setImporting(true);
    setError('');

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n').filter(line => line.trim() !== '');
        
        if (lines.length < 2) {
          throw new Error('Fișierul CSV trebuie să conțină cel puțin un header și o linie de date');
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        console.log('CSV Header:', header);

        // Validate required columns
        const requiredColumns = ['Denumire', 'Neck Size', 'Culoare'];
        const missingColumns = requiredColumns.filter(col => !header.includes(col));
        if (missingColumns.length > 0) {
          throw new Error(`Lipsesc coloanele obligatorii: ${missingColumns.join(', ')}`);
        }

        // Get station column indices
        const statieColumns = {};
        statiiLucru.forEach(statie => {
          const columnIndex = header.indexOf(statie.denumire);
          if (columnIndex !== -1) {
            statieColumns[statie.id] = columnIndex;
          }
        });

        console.log('Station columns mapping:', statieColumns);

        // Parse data rows
        const importedProducts = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          
          if (values.length < 3) continue; // Skip incomplete rows
          
          const denumireIndex = header.indexOf('Denumire');
          const neckSizeIndex = header.indexOf('Neck Size');
          const culoareIndex = header.indexOf('Culoare');

          const produs = {
            denumire: values[denumireIndex],
            neckSize: values[neckSizeIndex],
            culoare: values[culoareIndex],
            bucPerSac: values[header.indexOf('Buc/Sac')] ? parseInt(values[header.indexOf('Buc/Sac')]) || 0 : 0,
            createdBy: currentUser?.username,
            statiiLucru: {}
          };

          // Parse station values
          Object.entries(statieColumns).forEach(([statieId, columnIndex]) => {
            const value = values[columnIndex];
            produs.statiiLucru[statieId] = value ? parseFloat(value) || 0 : 0;
          });

          // Initialize missing stations with 0
          statiiLucru.forEach(statie => {
            if (!produs.statiiLucru.hasOwnProperty(statie.id)) {
              produs.statiiLucru[statie.id] = 0;
            }
          });

          importedProducts.push(produs);
        }

        console.log('Imported products:', importedProducts);

        // Send each product to the backend
        for (const produs of importedProducts) {
          await axios.post(`${API_BASE_URL}/produse`, produs);
        }

        // Refresh the product list
        await fetchProduse();
        
        setError(`${importedProducts.length} produse au fost importate cu succes!`);
        setTimeout(() => setError(''), 5000);

      } catch (error) {
        console.error('CSV import error:', error);
        setError(error.message || 'Eroare la importarea fișierului CSV');
      } finally {
        setImporting(false);
        // Reset file input
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setError('Eroare la citirea fișierului');
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const triggerFileInput = () => {
    if (isOperator) {
      setError('Operatorii nu pot importa produse.');
      return;
    }
    document.getElementById('csv-file-input').click();
  };

  // CRUD Functions for Products
  const handleAddProduct = () => {
    if (isOperator) {
      setError('Operatorii nu pot adăuga produse.');
      return;
    }
    setEditingProduct(null);
    setFormData({
      denumire: '',
      neckSize: '',
      culoare: '',
      statiiLucru: {}
    });
    setShowModal(true);
    setError('');
  };

  const handleEditProduct = (produs) => {
    if (isOperator) { setError('Operatorii nu pot edita produse'); return; }
    setEditingProduct(produs);
    setFormData({
      denumire: produs.denumire,
      neckSize: produs.neckSize,
      saci: produs.saci || 0,
      bucPerSac: produs.bucPerSac || 0,
      culoare: produs.culoare,
      statiiLucru: produs.statiiLucru || {}
    });
    setShowModal(true);
    setError('');
  };

  const handleDeleteProduct = async (produs) => {
    if (isOperator) { setError('Operatorii nu pot șterge produse'); return; }

    if (window.confirm(`Sigur doriți să ștergeți produsul "${produs.denumire}"?`)) {
      try {
        setLoading(true);
        await axios.delete(`${API_BASE_URL}/produse/${produs.id}`, {
          data: { currentUser: currentUser?.username }
        });
        
        setProduse(produse.filter(p => p.id !== produs.id));
        setError('Produsul a fost șters cu succes');
      } catch (error) {
        console.error('Error deleting product:', error);
        setError(error.response?.data?.error || 'Eroare la ștergerea produsului');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveProduct = async () => {
    if (isOperator) {
      setError('Operatorii nu pot salva modificări la produse.');
      return;
    }

    if (!formData.denumire.trim() || !formData.neckSize.trim() || !formData.culoare.trim()) {
      setError('Denumirea, neck size și culoarea sunt obligatorii');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (editingProduct) {
        // Edit existing product
        const response = await axios.put(`${API_BASE_URL}/produse/${editingProduct.id}`, {
          ...formData,
          currentUser: currentUser?.username
        });
        
        setProduse(produse.map(p => 
          p.id === editingProduct.id ? response.data : p
        ));
      } else {
        // Add new product
        const response = await axios.post(`${API_BASE_URL}/produse`, {
          ...formData,
          createdBy: currentUser?.username
        });
        
        setProduse([...produse, response.data]);
      }

      setShowModal(false);
      setFormData({
        denumire: '',
        neckSize: '',
        culoare: '',
        bucPerSac: 0,
        statiiLucru: {}
      });
      setEditingProduct(null);
      setError(editingProduct ? 'Produsul a fost actualizat cu succes' : 'Produsul a fost adăugat cu succes');
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.response?.data?.error || 'Eroare la salvarea produsului');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setFormData({
      denumire: '',
      neckSize: '',
      culoare: '',
      statiiLucru: {}
    });
    setEditingProduct(null);
    setError('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatieChange = (statieId, value) => {
    setFormData(prev => ({
      ...prev,
      statiiLucru: {
        ...prev.statiiLucru,
        [statieId]: parseInt(value) || 0
      }
    }));
  };

  const canUserEdit = () => {
    return !isOperator;
  };

  // Filter function
  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Handle multi-select for dropdowns
  const handleMultiSelectChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value) 
        ? prev[column].filter(item => item !== value)
        : [...prev[column], value]
    }));
  };

  // Get unique values for dropdown options
  const getUniqueValues = (field) => {
    return [...new Set(produse.map(produs => produs[field]))].sort();
  };

  // Toggle dropdown visibility
  const toggleDropdown = (field) => {
    setDropdownOpen(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen({ neckSize: false, culoare: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sort function
  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = 'default';
      } else {
        direction = 'asc';
      }
    }
    setSortConfig({ key: column, direction });
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '↕️';
    if (sortConfig.direction === 'asc') return '↑';
    if (sortConfig.direction === 'desc') return '↓';
    return '↕️';
  };

  // Filter and sort products
  const getFilteredAndSortedProducts = () => {
    let filtered = produse.filter(produs => {
      const denumireMatch = produs.denumire.toLowerCase().includes(filters.denumire.toLowerCase());
      const neckSizeMatch = filters.neckSize.length === 0 || filters.neckSize.includes(produs.neckSize);
      const culoareMatch = filters.culoare.length === 0 || filters.culoare.includes(produs.culoare);
      
      return denumireMatch && neckSizeMatch && culoareMatch;
    });

    if (sortConfig.key && sortConfig.direction !== 'default') {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key].toLowerCase();
        const bValue = b[sortConfig.key].toLowerCase();
        
        if (sortConfig.direction === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    return filtered;
  };

  // Get paginated products
  const getPaginatedProducts = () => {
    const filteredProducts = getFilteredAndSortedProducts();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    return Math.ceil(getFilteredAndSortedProducts().length / itemsPerPage);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setInputValue(newItemsPerPage.toString());
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  // CSV Export function
  const exportToCSV = () => {
    if (isOperator) {
      setError('Operatorii nu pot exporta produse.');
      return;
    }

    try {
      const headers = ['Denumire', 'Neck Size', 'Culoare'];
      
      // Add station headers using unique representatives (group stations by denumire)
      uniqueStatii.forEach(statie => {
        headers.push(statie.denumire);
      });
      
      headers.push('Creat de');

      // Get all filtered and sorted products (not just paginated)
      const exportData = getFilteredAndSortedProducts();

      // Create CSV content
      const csvContent = [
        headers.join(','), // Header row
        ...exportData.map(produs => {
          const row = [
            `"${produs.denumire}"`,
            `"${produs.neckSize}"`,
            `"${produs.culoare}"`
          ];
          
          // Add station values using representative ids (duplicates collapse to the representative column)
          uniqueStatii.forEach(statie => {
            // find all station ids that map to this representative and sum/choose the first non-zero
            const repId = stationRepMap[statie.id] || statie.id;
            row.push(produs.statiiLucru?.[repId] || 0);
          });
          
          row.push(`"${produs.createdBy}"`);
          
          return row.join(',');
        })
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `produse_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setError('✅ Export CSV realizat cu succes!');
      setTimeout(() => setError(''), 3000);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Eroare la exportul CSV');
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>📦 Produse</h2>
          {!isOperator && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={handleAddProduct}
                disabled={loading || importing}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                ➕ Adaugă Produs
              </button>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleCSVImport}
                style={{ display: 'none' }}
              />
              <button 
                className="btn btn-secondary"
                onClick={triggerFileInput}
                disabled={loading || importing}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {importing ? '📂 Importă...' : '📂 Importă CSV'}
              </button>
              <button 
                className="btn btn-success"
                onClick={exportToCSV}
                disabled={loading || importing || produse.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                📤 Exportă CSV
              </button>
            </div>
          )}
        </div>

        {error && (
          <div style={{ 
            background: error.includes('succes') ? '#d1e7dd' : '#f8d7da', 
            color: error.includes('succes') ? '#0f5132' : '#721c24', 
            padding: '0.75rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            border: error.includes('succes') ? '1px solid #badbcc' : '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {(filters.denumire || filters.neckSize.length > 0 || filters.culoare.length > 0) && (
          <div style={{ 
            background: '#e7f3ff', 
            color: '#0066cc', 
            padding: '0.5rem', 
            borderRadius: '4px', 
            marginBottom: '1rem',
            fontSize: '0.9rem',
            border: '1px solid #b3d9ff'
          }}>
            Se afișează {getPaginatedProducts().length} din {getFilteredAndSortedProducts().length} produse filtrate (total: {produse.length})
            {sortConfig.key && sortConfig.direction !== 'default' && 
              ` • Sortate după ${sortConfig.key} (${sortConfig.direction === 'asc' ? 'crescător' : 'descrescător'})`
            }
            {getTotalPages() > 1 && ` • Pagina ${currentPage} din ${getTotalPages()}`}
          </div>
        )}

        {/* Simple Filter Controls with Sort */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem',
          marginBottom: '1rem',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="Denumire"
              value={filters.denumire}
              onChange={(e) => handleFilterChange('denumire', e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.9rem',
                width: '150px',
                height: '2.5rem',
                boxSizing: 'border-box'
              }}
            />
            <button 
              onClick={() => handleSort('denumire')}
              style={{ 
                background: 'none', 
                border: '1px solid #ced4da', 
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '0.5rem',
                fontSize: '0.9rem',
                backgroundColor: sortConfig.key === 'denumire' ? '#007bff' : 'white',
                color: sortConfig.key === 'denumire' ? 'white' : 'black',
                height: '2.5rem',
                boxSizing: 'border-box'
              }}
            >
              {getSortIcon('denumire')}
            </button>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div className="dropdown-container" style={{ position: 'relative' }}>
              <div 
                onClick={() => toggleDropdown('neckSize')}
                style={{
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  height: '2.5rem',
                  width: '150px',
                  padding: '0.5rem',
                  fontSize: '0.9rem',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  boxSizing: 'border-box',
                  justifyContent: 'center'
                }}
              >
                {filters.neckSize.length === 0 ? (
                  <span style={{ color: '#6c757d' }}>Neck Size</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {filters.neckSize.map(size => (
                      <span key={size} style={{
                        background: '#007bff',
                        color: 'white',
                        padding: '0.1rem 0.3rem',
                        borderRadius: '3px',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        {size}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMultiSelectChange('neckSize', size);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {dropdownOpen.neckSize && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  backgroundColor: 'white',
                  fontSize: '0.9rem',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {getUniqueValues('neckSize').map(size => (
                    <div 
                      key={size} 
                      onClick={() => handleMultiSelectChange('neckSize', size)}
                      style={{
                        padding: '0.5rem',
                        cursor: 'pointer',
                        backgroundColor: filters.neckSize.includes(size) ? '#007bff' : 'white',
                        color: filters.neckSize.includes(size) ? 'white' : 'black',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => {
                        if (!filters.neckSize.includes(size)) {
                          e.target.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!filters.neckSize.includes(size)) {
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {size}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => handleSort('neckSize')}
              style={{ 
                background: 'none', 
                border: '1px solid #ced4da', 
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '0.5rem',
                fontSize: '0.9rem',
                backgroundColor: sortConfig.key === 'neckSize' ? '#007bff' : 'white',
                color: sortConfig.key === 'neckSize' ? 'white' : 'black',
                height: '2.5rem',
                boxSizing: 'border-box'
              }}
            >
              {getSortIcon('neckSize')}
            </button>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div className="dropdown-container" style={{ position: 'relative' }}>
              <div 
                onClick={() => toggleDropdown('culoare')}
                style={{
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  height: '2.5rem',
                  width: '150px',
                  padding: '0.5rem',
                  fontSize: '0.9rem',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  boxSizing: 'border-box',
                  justifyContent: 'center'
                }}
              >
                {filters.culoare.length === 0 ? (
                  <span style={{ color: '#6c757d' }}>Culoare</span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {filters.culoare.map(culoare => (
                      <span key={culoare} style={{
                        background: '#007bff',
                        color: 'white',
                        padding: '0.1rem 0.3rem',
                        borderRadius: '3px',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        {culoare}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMultiSelectChange('culoare', culoare);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {dropdownOpen.culoare && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  backgroundColor: 'white',
                  fontSize: '0.9rem',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {getUniqueValues('culoare').map(culoare => (
                    <div 
                      key={culoare} 
                      onClick={() => handleMultiSelectChange('culoare', culoare)}
                      style={{
                        padding: '0.5rem',
                        cursor: 'pointer',
                        backgroundColor: filters.culoare.includes(culoare) ? '#007bff' : 'white',
                        color: filters.culoare.includes(culoare) ? 'white' : 'black',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => {
                        if (!filters.culoare.includes(culoare)) {
                          e.target.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!filters.culoare.includes(culoare)) {
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {culoare}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => handleSort('culoare')}
              style={{ 
                background: 'none', 
                border: '1px solid #ced4da', 
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '0.5rem',
                fontSize: '0.9rem',
                backgroundColor: sortConfig.key === 'culoare' ? '#007bff' : 'white',
                color: sortConfig.key === 'culoare' ? 'white' : 'black',
                height: '2.5rem',
                boxSizing: 'border-box'
              }}
            >
              {getSortIcon('culoare')}
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Denumire</th>
                <th>Neck Size</th>
                <th>Culoare</th>
                <th>Buc/Sac</th>
                {uniqueStatii.map(statie => (
                  <th key={statie.id}>{statie.denumire}</th>
                ))}
                <th>Creat de</th>
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedProducts().map(produs => (
                <tr key={produs.id}>
                  <td>{produs.denumire}</td>
                  <td>{produs.neckSize}</td>
                  <td>{produs.culoare}</td>
                  <td>{produs.bucPerSac || 0}</td>
                  {uniqueStatii.map(statie => (
                    <td key={statie.id}>
                      {produs.statiiLucru?.[stationRepMap[statie.id] || statie.id] || 0}
                    </td>
                  ))}
                  <td>{produs.createdBy}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className={`btn ${canUserEdit(produs) ? 'btn-warning' : 'btn-disabled'}`}
                        onClick={() => handleEditProduct(produs)}
                        disabled={!canUserEdit(produs) || loading}
                        style={{ 
                          fontSize: '0.8rem', 
                          padding: '0.25rem 0.5rem',
                          opacity: canUserEdit(produs) ? 1 : 0.5,
                          cursor: canUserEdit(produs) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        ✏️ Editează
                      </button>
                      <button
                        className={`btn ${canUserEdit(produs) ? 'btn-danger' : 'btn-disabled'}`}
                        onClick={() => handleDeleteProduct(produs)}
                        disabled={!canUserEdit(produs) || loading}
                        style={{ 
                          fontSize: '0.8rem', 
                          padding: '0.25rem 0.5rem',
                          background: canUserEdit(produs) ? '#e74c3c' : '#ccc',
                          opacity: canUserEdit(produs) ? 1 : 0.5,
                          cursor: canUserEdit(produs) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        🗑️ Șterge
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {getFilteredAndSortedProducts().length === 0 && produse.length > 0 && (
          <p style={{ textAlign: 'center', color: '#666', margin: '2rem 0' }}>
            Nu s-au găsit produse care să corespundă filtrelor aplicate.
          </p>
        )}

        {produse.length === 0 && (
          <p style={{ textAlign: 'center', color: '#666', margin: '2rem 0' }}>
            Nu există produse adăugate. Folosiți butonul "Adaugă Produs" sau funcția "Importă CSV" pentru a adăuga produse.
          </p>
        )}

        {/* Pagination */}
        {getFilteredAndSortedProducts().length > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '1rem', 
            padding: '1rem 0',
            borderTop: '1px solid #dee2e6'
          }}>
            {/* Items per page selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Produse per pagină:</span>
              <input
                type="number"
                min="1"
                max="1000"
                value={inputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setInputValue(value); // Always update the input display
                  
                  if (value === '') {
                    // Allow empty input temporarily
                    return;
                  }
                  
                  const numValue = Number(value);
                  if (numValue >= 1 && numValue <= 1000) {
                    setItemsPerPage(numValue);
                    setCurrentPage(1);
                  }
                }}
                onBlur={(e) => {
                  // When user leaves the input, ensure we have a valid value
                  if (e.target.value === '' || Number(e.target.value) < 1 || Number(e.target.value) > 1000) {
                    setInputValue(itemsPerPage.toString());
                  }
                }}
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  width: '80px',
                  backgroundColor: 'white',
                  textAlign: 'center'
                }}
              />
              <span style={{ fontSize: '0.8rem', color: '#666' }}>(1-1000)</span>
              
              {/* Quick buttons for common values */}
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {[10, 25, 50, 100].map(value => (
                  <button
                    key={value}
                    onClick={() => handleItemsPerPageChange(value)}
                    style={{
                      padding: '0.2rem 0.4rem',
                      border: '1px solid #ced4da',
                      borderRadius: '3px',
                      fontSize: '0.8rem',
                      backgroundColor: itemsPerPage === value ? '#007bff' : 'white',
                      color: itemsPerPage === value ? 'white' : '#007bff',
                      cursor: 'pointer',
                      minWidth: '25px'
                    }}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            {/* Page navigation */}
            {getTotalPages() > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#007bff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ««
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#007bff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  «
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and 2 pages around current
                    return page === 1 || 
                           page === getTotalPages() || 
                           Math.abs(page - currentPage) <= 2;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {/* Add ellipsis if there's a gap */}
                      {index > 0 && array[index - 1] < page - 1 && (
                        <span style={{ padding: '0.5rem', color: '#6c757d' }}>...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          backgroundColor: currentPage === page ? '#007bff' : 'white',
                          color: currentPage === page ? 'white' : '#007bff',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: currentPage === page ? 'bold' : 'normal'
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === getTotalPages()}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: currentPage === getTotalPages() ? '#f8f9fa' : 'white',
                    color: currentPage === getTotalPages() ? '#6c757d' : '#007bff',
                    cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  »
                </button>
                <button
                  onClick={() => handlePageChange(getTotalPages())}
                  disabled={currentPage === getTotalPages()}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: currentPage === getTotalPages() ? '#f8f9fa' : 'white',
                    color: currentPage === getTotalPages() ? '#6c757d' : '#007bff',
                    cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  »»
                </button>
              </div>
            )}

            {/* Page info */}
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {getTotalPages() > 1 && `Pagina ${currentPage} din ${getTotalPages()}`}
            </div>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Product */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            maxWidth: '600px',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ 
              marginBottom: '1.5rem', 
              color: '#333',
              borderBottom: '2px solid #007bff',
              paddingBottom: '0.5rem'
            }}>
              {editingProduct ? 'Editează Produsul' : 'Adaugă Produs Nou'}
            </h3>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#555'
              }}>
                Denumire *
              </label>
              <input
                type="text"
                value={formData.denumire}
                onChange={(e) => handleFormChange('denumire', e.target.value)}
                placeholder="Introduceți denumirea produsului"
                style={{ 
                  width: '100%',
                  padding: '0.75rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#555'
              }}>
                Neck Size *
              </label>
              <input
                type="text"
                value={formData.neckSize}
                onChange={(e) => handleFormChange('neckSize', e.target.value)}
                placeholder="ex: 25mm"
                style={{ 
                  width: '100%',
                  padding: '0.75rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#555'
              }}>
                Culoare *
              </label>
              <input
                type="text"
                value={formData.culoare}
                onChange={(e) => handleFormChange('culoare', e.target.value)}
                placeholder="ex: Albastru"
                style={{ 
                  width: '100%',
                  padding: '0.75rem', 
                  borderRadius: '4px', 
                  border: '1px solid #ced4da',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                disabled={loading}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#555' }}>Buc/Sac</label>
                <input
                  type="number"
                  min="0"
                  value={formData.bucPerSac}
                  onChange={(e) => handleFormChange('bucPerSac', parseInt(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Stații de lucru section */}
            {statiiLucru.length > 0 && (
              <div className="form-group">
                <label>Numărul de bucăți pentru stațiile de lucru</label>
                <div style={{ 
                  border: '1px solid #ced4da', 
                  borderRadius: '4px', 
                  padding: '1rem', 
                  marginBottom: '1rem',
                  backgroundColor: '#f8f9fa'
                }}>
                  {statiiLucru.map(statie => (
                    <div key={statie.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontWeight: '500' }}>{statie.denumire}:</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.statiiLucru[statie.id] || 0}
                        onChange={(e) => handleStatieChange(statie.id, e.target.value)}
                        style={{ 
                          width: '80px', 
                          padding: '0.25rem',
                          textAlign: 'right'
                        }}
                        disabled={loading}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#666', marginLeft: '0.5rem' }}>buc</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div style={{ 
                background: error.includes('succes') ? '#d1e7dd' : '#f8d7da', 
                color: error.includes('succes') ? '#0f5132' : '#721c24', 
                padding: '0.5rem', 
                borderRadius: '4px', 
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary" 
                onClick={handleCancelEdit}
                disabled={loading}
              >
                Anulează
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleSaveProduct}
                disabled={loading || !formData.denumire.trim() || !formData.neckSize.trim() || !formData.culoare.trim()}
              >
                {loading ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produse;
